import { ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import styles from './playerDetails.module.css';
import WavyConnectorLine from '../wavyConnectorLine/wavyConnectorLine';
import {
  ActivityButtons,
  PlayerDetailsProps,
  ProcessedSweep,
  ProcessSweepDataResult,
} from './playerDetails.types';
import PlayerIcon from '../playerIcon/playerIcon';
import getStartAndEndOfDay, {
  convertMinutesToCompactFormat,
  decodeAndFormatPlayerDescription,
  parseVisitActivityATMData,
  parseVisitActivityGamblingData,
} from '../../utils/helper';
import { PlayerRisk } from '../playerIcon/playerIcon.types';
import { Trans, useTranslation } from 'react-i18next';
import { useVisitActivity } from '../../hooks/useVisitActivity';
import { useOrganizationContext } from '../../service/context/organizationContext';
import {
  Location,
  Player,
  POIClassification,
  ReportType,
  VisitType,
  GetPlayerHarmCountQueryParams,
  GetPlayerInterventionCountQueryParams,
} from '@inplay/models';
import GamblingIcon from '../../assets/exportableIcons/gamblingSession';
import Button from '../button/button';
import {
  ParsedATMVisit,
  ParsedVisitGamblingActivity,
  Sweep,
} from '../../utils/helper.type';
import {
  getGamingVisitTimes,
  PlayerDetailsATMUseCard,
  PlayerDetailsGamblingSessionsCard,
  PlayerDetailsSweepPresenceCard,
  processSweepData,
} from './playerDetailsHelper';
import SweepIcon from '../../assets/exportableIcons/sweepIcon';
import moment from 'moment';
import atmUseInactive from '../../assets/exportableIcons/atmUse.svg';
import atmUseActive from '../../assets/exportableIcons/atmUseActive.svg';
import SweepViewModal from '../sweepViewModal/sweepViewModal';
import SweepViewReport from '../../pages/sweeps/sweepViewReport';
import SweepEditModal from '../sweepEditModal/sweepEditModal';
import { useUsersSummary } from '../../hooks/useUserData';
import { useSweepsSummary } from '../../hooks/useSweepData';
import UserVerificationModal from '../userVerificationModal/userVerificationModal';
import { SelectedUser } from '../userVerificationModal/userVerificationModal.types';
import InterventionReportForm from '../interventionReportForm/interventionReportForm';
import LinkHarmReportsModal from '../linkHarmReportsModal/linkHarmReportsModal';
import { useResponsiveLayout } from '../../hooks/useResponsiveLayout';
import HarmReportForm from '../harmReportForm/harmReportForm';
import { ReportService } from '../../service/reportService';
import Loading from '../../assets/loading/loading';

const PlayerDetails = ({ player }: PlayerDetailsProps) => {
  const { t } = useTranslation();
  const [activeSwitch, setActiveSwitch] = useState<number>(0); // Stores the active switch index
  const [activeCard, setActiveCard] = useState<number>(0); // Stores the active card index
  const [expandedCards, setExpandedCards] = useState<number[]>([]); // Stores the indexes of expanded cards
  const switchRefs = [
    useRef<HTMLButtonElement>(null),
    useRef<HTMLButtonElement>(null),
    useRef<HTMLButtonElement>(null),
  ];
  const activityCardsRef = useRef<HTMLDivElement>(null);
  const cardRefs = useRef<(HTMLDivElement | null)[]>([]);
  const containerRef = useRef<HTMLDivElement>(null);
  const [lines, setLines] = useState<
    { x1: number; y1: number; x2: number; y2: number }[]
  >([]);
  const { selectedVenueBranch } = useOrganizationContext();
  const selectedBranchIDs: string[] = useMemo(() => {
    return selectedVenueBranch ? [selectedVenueBranch._id] : [];
  }, [selectedVenueBranch]);
  const { visitActivities, visitLoading } = useVisitActivity(
    player.playerId,
    selectedBranchIDs,
  );
  const [selectedSweepForModal, setSelectedSweepForModal] = useState<
    Sweep | undefined
  >(undefined);
  const [showSweepViewModal, setShowSweepViewModal] = useState<boolean>(false);
  const [showSweepEditModal, setShowSweepEditModal] = useState<boolean>(false);
  const [showUserVerification, setShowUserVerification] =
    useState<boolean>(false);
  const [verifiedUser, setVerifiedUser] = useState<SelectedUser | null>(null);
  const [
    showCreateInterventionReportModal,
    setShowCreateInterventionReportModal,
  ] = useState<boolean>(false);
  const [showCreateHarmReportModal, setShowCreateHarmReportModal] =
    useState<boolean>(false);
  const [showLinkHarmReportsModal, setShowLinkHarmReportsModal] =
    useState<boolean>(false);
  const [linkedHarmReportIds, setLinkedHarmReportIds] = useState<string[]>([]);
  const [linkedHarmRefNos, setLinkedHarmRefNos] = useState<string[]>([]);

  const { usersSummary } = useUsersSummary(true);
  const { isTablet } = useResponsiveLayout();

  const [cardsData, setCardsData] = useState<
    Array<
      ParsedVisitGamblingActivity | ParsedATMVisit | ProcessedSweep | undefined
    >
  >([]);
  const [createReportFor, setCreateReportFor] = useState<
    ReportType | undefined
  >(undefined);
  const { fetchSweepSummary, sweepsSummary } = useSweepsSummary({
    startTime: getStartAndEndOfDay(new Date()).startTime,
    endTime: getStartAndEndOfDay(new Date()).endTime,
    venueBranchIds: selectedBranchIDs,
  });

  const [processedSweepSummary, setProcessedSweepSummary] =
    useState<ProcessSweepDataResult>();

  const reportService: ReportService = useMemo(() => new ReportService(), []);
  const [harmCount, setHarmCount] = useState<number | null>(null);
  const [interventionOffered, setInterventionOffered] = useState<number | null>(
    null,
  );
  const [interventionRequested, setInterventionRequested] = useState<
    number | null
  >(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  const fetchPlayerCountDetails = async () => {
    try {
      setIsLoading(true);
      console.log(isLoading);
      // If no selected Venue Branch
      if (!selectedVenueBranch) return;

      // If there is no player data received
      if (!player) return;

      const currentDate = new Date();
      const utcDate = new Date(
        Date.UTC(
          currentDate.getFullYear(),
          currentDate.getMonth(),
          currentDate.getDate(),
        ),
      );

      const harmQuery: GetPlayerHarmCountQueryParams = {
        venueBranchId: selectedVenueBranch._id,
        currentDate: utcDate.toISOString(),
      };
      const { harmCount } = await reportService.getPlayerHarmCount(
        player.playerId,
        harmQuery,
      );
      setHarmCount(harmCount);

      const interventionQuery: GetPlayerInterventionCountQueryParams = {
        venueBranchId: selectedVenueBranch._id,
        currentDate: utcDate.toISOString(),
      };
      const { interventionOffered, interventionRequested } =
        await reportService.getPlayerInterventionCount(
          player.playerId,
          interventionQuery,
        );
      setInterventionOffered(interventionOffered);
      setInterventionRequested(interventionRequested);
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchSweepSummary();
    fetchPlayerCountDetails();
  }, []);

  useEffect(() => {
    if (!selectedBranchIDs.length) return;
  }, [selectedBranchIDs]);

  useEffect(() => {
    const processedData: ProcessSweepDataResult = processSweepData(
      player.playerId,
      sweepsSummary,
      selectedVenueBranch?.sweepIntervalInMinutes || 20,
      visitActivities,
    );

    setProcessedSweepSummary(processedData);
  }, [sweepsSummary, visitActivities]);

  const activityButtons: ActivityButtons = useMemo(
    () => [
      {
        label: 'playerDetails.gamblingSessions',
        icon: <GamblingIcon active={activeSwitch === 0} height={'40%'} />,
      },
      {
        label: 'playerDetails.atmWithdrawals',
        icon: (
          <img
            src={activeSwitch === 1 ? atmUseActive : atmUseInactive}
            height={'40%'}
            alt=""
          />
        ),
      },
      {
        label: 'playerDetails.sweepPresence',
        icon: <SweepIcon active={activeSwitch === 2} height={'40%'} />,
      },
    ],
    [activeSwitch],
  );

  const playerDetails: { label: string; value: ReactNode }[] = useMemo(
    () => [
      {
        label: 'playerDetails.status',
        value:
          player.risk === PlayerRisk.LOW ? (
            <Trans i18nKey="dashboardNew.lowRisk" />
          ) : player.risk === PlayerRisk.INTERVENTION_OFFERED ? (
            <Trans i18nKey="dashboardNew.interventionOffer" />
          ) : player.risk === PlayerRisk.INTERVENTION_REQUIRED ? (
            <Trans i18nKey="dashboardNew.intervention" />
          ) : (
            <Trans i18nKey="dashboardNew.excluded" />
          ),
      },
      {
        label: 'playerDetails.name',
        value: '-',
      },
      {
        label: 'playerDetails.description',
        value: decodeAndFormatPlayerDescription(player.description),
      },
      {
        label: 'playerDetails.currentUninterrupted',
        value: (
          <>
            {convertMinutesToCompactFormat(
              getGamingVisitTimes(player.gamingVisits)
                .currentUninterruptedSession,
            )}

            {!(player.gamingVisits.at(-1)?.endTime ?? null) && (
              <span>
                {' '}
                - <Trans i18nKey="playerDetails.ongoing" />
              </span>
            )}
          </>
        ),
      },
      {
        label: 'playerDetails.gamblingAtmUse',
        value: player.totalWithdrawals,
      },
      {
        label: 'playerDetails.currentConsecutiveSweep',
        value: processedSweepSummary?.currentConsecutiveSweeps.toString(),
      },
      {
        label: 'playerDetails.totalInterventions',
        value: interventionOffered,
      },
      {
        label: 'playerDetails.totalTimeInGambling',
        value: convertMinutesToCompactFormat(
          getGamingVisitTimes(player.gamingVisits).totalTime,
        ),
      },
      {
        label: 'playerDetails.totalTimeOutside',
        value: convertMinutesToCompactFormat(
          getGamingVisitTimes(player.gamingVisits).totalTimeOutside,
        ),
      },
      {
        label: 'playerDetails.firstEntered',
        value: moment(player.firstEntered).format('HH:mm A'),
      },
      {
        label: 'playerDetails.lastExited',
        value: player.lastExited
          ? moment(player.lastExited).format('HH:mm A')
          : '-',
      },
    ],
    [player, activeSwitch, isLoading],
  );

  useEffect(() => {
    if (visitLoading || visitActivities.length === 0) return;
    setExpandedCards([]);
    if (activeSwitch === 0) {
      const parsedData: ParsedVisitGamblingActivity[] =
        parseVisitActivityGamblingData(
          visitActivities.filter(
            (activity) => activity.location === Location.GAMING,
          ),
        );
      setCardsData([parsedData[0], ...parsedData]);
    } else if (activeSwitch === 1) {
      const parsedAtmData: ParsedATMVisit[] =
        parseVisitActivityATMData(visitActivities);
      if (parsedAtmData.length > 0) {
        setCardsData([
          {
            visitType: VisitType.GAMING,
            location: Location.ATM,
            startTime: '',
            endTime: '',
            firstGamingSessionStartTime: '',
            poiClassification: parsedAtmData
              .filter((data) => data.visitType === VisitType.GAMING)
              .length.toString(), // This is just a dummy card to make the line visible for the top card, sending the total number of ATM cards in poiClassification just to get the value.
            name: 'Gambling Use',
          },
          ...parsedAtmData,
        ]);
      } else {
        setCardsData([undefined]);
      }
    } else if (activeSwitch === 2) {
      if (!processedSweepSummary) return;

      if (processedSweepSummary.processedData.length > 0) {
        setCardsData([
          {
            hourSequence: processedSweepSummary.currentConsecutiveSweeps,
            hourRange: 'Sweep Presence',
            gamblingDurationInMinutes: '0',
            breakInMinutes: '0',
            sweeps: [],
          },
          ...processedSweepSummary.processedData.reverse(),
        ]);
      } else {
        setCardsData([undefined]);
      }
    } else {
      setCardsData([undefined]);
    }
  }, [visitActivities, visitLoading, activeSwitch, sweepsSummary]);

  const updateLines = () => {
    const switchEl = switchRefs[activeSwitch].current;
    const container = containerRef.current;
    if (!switchEl || !container) return;

    const switchRect = switchEl.getBoundingClientRect();
    const containerRect = container.getBoundingClientRect();

    const startX = switchRect.right - containerRect.left;
    const startY = switchRect.top + switchRect.height / 2 - containerRect.top;

    const newLines = cardRefs.current
      .slice(0, cardsData.length)
      .map((card) => {
        if (!card) return null;
        const cardRect = card.getBoundingClientRect();
        const endX = cardRect.left - containerRect.left;
        const endY = cardRect.top + cardRect.height / 2 - containerRect.top;

        return { x1: startX, y1: startY, x2: endX, y2: endY };
      })
      .filter(Boolean) as typeof lines;

    setLines(newLines);
  };

  useEffect(() => {
    const scrollContainer = activityCardsRef.current;
    if (!scrollContainer) return;
    updateLines();
    scrollContainer.addEventListener('scroll', updateLines);
    window.addEventListener('resize', updateLines);
    return () => {
      scrollContainer.removeEventListener('scroll', updateLines);
      window.removeEventListener('resize', updateLines);
    };
  }, [activeSwitch, cardsData, activeCard, expandedCards]);

  const handleViewSweep = (sweepId: string) => {
    setSelectedSweepForModal(
      sweepsSummary.find((sweep) => sweep._id === sweepId),
    );
    setShowSweepViewModal(true);
  };

  const handleOnVerify = (user: SelectedUser) => {
    setVerifiedUser(user);
    if (createReportFor === ReportType.HARM) {
      setShowCreateHarmReportModal(true);
    } else if (createReportFor === ReportType.INTERVENTION) {
      setShowLinkHarmReportsModal(true);
    }
  };

  if (isLoading)
    return (
      <div className={styles.loading}>
        <Loading />
      </div>
    );

  return (
    <div className={styles.playerDetailsContainer}>
      {showSweepViewModal && (
        <SweepViewModal
          onEdit={() => {
            setShowSweepEditModal(true);
            setShowSweepViewModal(false);
          }}
          onDownload={() => { }}
          onClose={() => setShowSweepViewModal(false)}
        >
          <div>
            <SweepViewReport
              pdfContentData={{
                players: selectedSweepForModal?.players ?? [],
                time: selectedSweepForModal?.createdOn
                  ? moment(selectedSweepForModal?.createdOn).format('HH:mm')
                  : '-',
                hour: selectedSweepForModal?.hour,
                noOfPlayers: selectedSweepForModal?.players.filter(
                  (player: Player) => player.presentDuringSweep,
                ).length,
                harmObserved:
                  (selectedSweepForModal?.players?.filter(
                    (player: Player) =>
                      player.poiClassification === POIClassification.POI,
                  ).length ?? 0) > 0 &&
                  `Yes (${selectedSweepForModal?.players?.filter(
                    (player: Player) =>
                      player.poiClassification === POIClassification.POI,
                  ).length ?? 0
                  })`,
                tag: selectedSweepForModal?.tag,
                sweepType: selectedSweepForModal?.type,
                sweepStatus: selectedSweepForModal?.status,
                completeBySignature:
                  selectedSweepForModal?.completedBy?.signature ?? '',

                completeBySignatureDate: moment(
                  selectedSweepForModal?.completedBy?.signedDate,
                ).format('hh:mm A, DD/MM/YYYY'),
                reviewedBySignature:
                  selectedSweepForModal?.reviewedBy?.signature ?? '',

                reviewedBySignatureDate: moment(
                  selectedSweepForModal?.reviewedBy?.signedDate,
                ).format('hh:mm A, DD/MM/YYYY'),
                sweepNo: selectedSweepForModal?.sweepNumber,
                sweepDate: moment(selectedSweepForModal?.createdOn).format(
                  'DD/MM/YYYY',
                ),
                completedBy: selectedSweepForModal?.completedBy,
                reviewedBy: selectedSweepForModal?.reviewedBy,
              }}
              onPlayerHtmlGenerated={() => { }}
            />
          </div>
        </SweepViewModal>
      )}
      {showSweepEditModal && (
        <SweepEditModal
          footerButtonText={'userManagement.save'}
          onCloseEditModal={() => {
            setShowSweepEditModal(false);
            setShowSweepViewModal(true);
          }}
          usersSummary={usersSummary}
          selectedSweep={selectedSweepForModal}
          setSelectedSweep={setSelectedSweepForModal}
        />
      )}
      {showUserVerification && (
        <UserVerificationModal
          show={showUserVerification}
          onToggle={() => setShowUserVerification((prev) => !prev)}
          onVerified={handleOnVerify}
          scope={createReportFor === ReportType.HARM ? 'hrmc' : 'intc'}
        />
      )}
      {showLinkHarmReportsModal && (
        <LinkHarmReportsModal
          playerId={player.playerId}
          onClose={() => setShowLinkHarmReportsModal(false)}
          onSkip={() => {
            setShowLinkHarmReportsModal(false);
            setLinkedHarmReportIds([]);
            setShowCreateInterventionReportModal(true);
          }}
          onLink={(ids, refNos) => {
            setShowLinkHarmReportsModal(false);
            setLinkedHarmReportIds(ids);
            setLinkedHarmRefNos(refNos);
            setShowCreateInterventionReportModal(true);
          }}
        />
      )}
      {
        showCreateInterventionReportModal && (
          <InterventionReportForm
            title={t('createInterventionReport.title.new')}
            data={null}
            player={player}
            harmCount={harmCount}
            interventionOffered={interventionOffered}
            interventionRequested={interventionRequested}
            consecutiveSweeps={processedSweepSummary?.currentConsecutiveSweeps.toString()}
            isReviewAndEditMode={false}
            verifiedUser={verifiedUser}
            onBack={() => setShowCreateInterventionReportModal(false)}
            linkedHarmReportIds={linkedHarmReportIds}
            linkedHarmRefNos={linkedHarmRefNos}
          />
        )
      }
      {
        showCreateHarmReportModal && (
          <HarmReportForm
            title={t('createHarmReport.title.new')}
            data={null}
            player={player}
            harmCount={harmCount}
            interventionOffered={interventionOffered}
            interventionRequested={interventionRequested}
            consecutiveSweeps={processedSweepSummary?.currentConsecutiveSweeps.toString()}
            isReviewAndEditMode={false}
            verifiedUser={verifiedUser}
            onBack={() => setShowCreateHarmReportModal(false)}
          />
        )
      }
      <div className={styles.left}>
        <div className={styles.images}>
          <div className={styles.faceImage}>
            <PlayerIcon player={player} onClick={() => { }} noAnimation />
          </div >
          <div className={styles.fullImage}>
            <img src={player.image} alt="" />
          </div>
        </div >
        <div className={styles.details}>
          <h2 className={styles.playerID}>
            <Trans i18nKey="playerDetails.player" /> #{player.playerId}
          </h2>
          {playerDetails.map((detail) => (
            <>
              <div className={styles.dataCell}>
                <div className={styles.label}>
                  <Trans i18nKey={detail.label} />
                </div>
                <div className={styles.value}>{detail.value}</div>
              </div>
              <div className={styles.divider} />
            </>
          ))}
          <div
            className={`${styles.buttonsGroup} ${isTablet && styles.tablet}`}
          >
            <div className="flex gap-5">
              <Button
                onClick={() => {
                  setCreateReportFor(ReportType.HARM);
                  setShowUserVerification(true);
                }}
                className={styles.button}
                style={{ border: '1px solid #A3A7FF', backgroundColor: '#FFFFFF', color: '#561BDC' }}
              >
                <Trans i18nKey="playerDetails.reportHarm" />
              </Button>
              <Button
                onClick={() => {
                  setCreateReportFor(ReportType.INTERVENTION);
                  setShowUserVerification(true);
                }}
                className={styles.button}
              >
                <Trans i18nKey="playerDetails.recordIntervention" />
              </Button>
            </div>
            <Button
              onClick={() => { }}
              disabled
              className={`${styles.button} bg-[#BF1C2E] hover:bg-[#BF1C2E]`}
            >
              <Trans i18nKey="playerDetails.excludePlayer" />
            </Button>
          </div>
        </div>
      </div >
      <div ref={containerRef} className={`${styles.right}`}>
        <div className="flex flex-col items-center space-y-6">
          {activityButtons.map((button, idx) => (
            <span
              key={idx}
              className={`${styles.switch} ${activeSwitch === idx ? 'text-[#1A1A1A]' : 'text-[#6F6F6F]'
                }`}
            >
              <button
                ref={switchRefs[idx]}
                className={`${styles.switchIcon} ${button.disabled
                  ? 'bg-[#D5D5D5]'
                  : activeSwitch === idx
                    ? 'bg-PLAYER_DETAIL_ICON_THEME_COLOR'
                    : 'bg-PLAYER_DETAIL_ACTIVITY_CARD_THEME_COLOR'
                  }`}
                onClick={() => {
                  if (activeSwitch === idx || button.disabled) return;
                  setActiveSwitch(idx);
                }}
              >
                {button.icon}
              </button>
              <Trans i18nKey={button.label} />
            </span>
          ))}
        </div>

        <div className={styles.activityCards} ref={activityCardsRef}>
          {visitLoading ? (
            <div className={styles.loader}>
              <img src="./loader.gif" alt="" width={'100px'} />
            </div>
          ) : (
            cardsData.map(
              (
                activity:
                  | ParsedVisitGamblingActivity
                  | ParsedATMVisit
                  | ProcessedSweep
                  | undefined,
                idx: number,
              ) => {
                if (activeSwitch === 0 && activity === undefined) return null;
                const isFirst: boolean = idx === 0;
                const isActive: boolean = idx === activeCard;
                const isExpanded: boolean = expandedCards.includes(idx);
                const allExpanded: boolean =
                  expandedCards.length === cardsData.length - 1;
                return (
                  <div
                    key={idx}
                    ref={(el) => (cardRefs.current[idx] = el)}
                    className={`${styles.activityCardWrapper}`}
                    onClick={() => {
                      if (isExpanded) {
                        setExpandedCards((prev) =>
                          prev.filter((card: number) => card !== idx),
                        );
                      }
                      if (isActive) {
                        setActiveCard(0);
                      } else {
                        setActiveCard(idx);
                        if (idx !== 0)
                          setExpandedCards((prev) => [...prev, idx]);
                      }
                    }}
                  >
                    {activeSwitch === 0 ? (
                      <PlayerDetailsGamblingSessionsCard
                        isExpanded={isExpanded}
                        isFirst={isFirst}
                        activity={activity as ParsedVisitGamblingActivity}
                        handleExpandAll={() => {
                          if (allExpanded) {
                            setExpandedCards([]);
                          } else {
                            setExpandedCards(
                              cardsData
                                .map((_, index) => index)
                                .filter((_, index) => index !== 0),
                            );
                          }
                        }}
                        allExpanded={allExpanded}
                      />
                    ) : activeSwitch === 1 ? (
                      <PlayerDetailsATMUseCard
                        isFirst={isFirst}
                        isExpanded={isExpanded}
                        activity={activity as ParsedATMVisit | undefined}
                        handleExpandAll={() => {
                          if (allExpanded) {
                            setExpandedCards([]);
                          } else {
                            setExpandedCards(
                              cardsData
                                .map((_, index) => index)
                                .filter((_, index) => index !== 0),
                            );
                          }
                        }}
                        isOnGoing={
                          activity
                            ? (activity as ParsedATMVisit)
                              .firstGamingSessionStartTime === null &&
                            idx === cardsData.length - 1
                            : false
                        }
                        allExpanded={allExpanded}
                      />
                    ) : activeSwitch === 2 ? (
                      <PlayerDetailsSweepPresenceCard
                        isFirst={isFirst}
                        isExpanded={isExpanded}
                        sweepsData={activity as ProcessedSweep}
                        handleExpandAll={() => {
                          if (allExpanded) {
                            setExpandedCards([]);
                          } else {
                            setExpandedCards(
                              cardsData
                                .map((_, index) => index)
                                .filter((_, index) => index !== 0),
                            );
                          }
                        }}
                        allExpanded={allExpanded}
                        handleViewSweep={handleViewSweep}
                      />
                    ) : (
                      <></>
                    )}
                  </div>
                );
              },
            )
          )}
        </div>

        {lines.map((line, idx) => (
          <WavyConnectorLine
            key={idx}
            line={line}
            active={idx === activeCard}
          />
        ))}
      </div>
    </div >
  );
};

export default PlayerDetails;
